import {css, html, LitElement, nothing} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {clone} from 'ramda';

import {Activity} from './activity';
import {Batch} from './batch';
import Decimal from './break_infinity';
import global_css from './global-css';
import {landmarkNames} from './initializeLandmarks';
import {ToolTippy} from './mixins/tooltippy';
import {BoundValue} from './panel';
import {ri} from './runner-idle';
import {formatCostEffect, formatTime} from './utils';

export interface Configuration {
  // #region Public Indexers (1)

  [key: PropertyKey]:
    | boolean
    | string
    | {
        value: number;
        min: number;
        max: number;
      };

  // #endregion Public Indexers (1)
}

@customElement('ri-activity-queue-item')
export class ActivityQueueItem extends ToolTippy(LitElement, {
  placement: 'top-start',
  hideOnClick: false,
}) {
  // #region Properties (14)

  private _activity: Activity = new Activity();

  public static override styles = [
    global_css,
    css`
      :host {
        width: 100%;
        background-color: rgb(var(--value-color, initial), 0.2);
      }

      [data-active] {
        background-color: #fff8;
      }

      [data-disabled] {
        background-color: #0004;
      }

      button {
        font-weight: bold;
        font-size: 1rem;
      }

      .queue-item,
      .drag-helper {
        display: flex;
        width: 100%;
        justify-content: space-between;
        transition: background-color 0.1s ease-in-out;
      }

      .queue-item > *,
      .drag-helper > * {
        padding: 4px;
      }

      .queue-item smooth-progress,
      .drag-helper smooth-progress,
      .queue-item progress,
      .drag-helper progress {
        width: 25%;
      }

      .queue-item-label {
        width: 50%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .queue-item-label-header {
        width: 50%;
        overflow-x: auto;
      }

      .drag-hover {
        background: #0002;
      }

      .drag-handle {
        padding: 2px;
        padding-top: 5px; // Manually centering braille icon. Ew.
      }

      label {
        font-size: 1rem;
      }

      .repeat {
        font-size: 1rem;
        padding: 4px;
      }
      .repeat::before {
        content: 'x';
        padding-right: 2px;
      }
      .queue-tools {
        width: 90px;
      }
      input {
        width: 12ch;
      }
    `,
  ];

  @property({type: Boolean, reflect: true}) public active = false;
  @property({type: Number}) public activeIndex = 0;
  @property() public option = '';
  @property({type: Number}) public repeat = 1;
  @property({type: Boolean}) public _repeatActivity = true;
  repeatActivityInput: HTMLElement | undefined;
  public get repeatActivity() {
    return this._repeatActivity;
  }
  public set repeatActivity(value) {
    this._repeatActivity = value;
    setTimeout(this.updateRepeatActivity.bind(this), 100);
  }
  updateRepeatActivity() {
    if (!this.repeatActivityInput) {
      this.repeatActivityInput =
        this.shadowRoot?.getElementById('repeat-activity-input') ?? undefined;
    }
    if (this.repeatActivityInput instanceof HTMLInputElement) {
      this.repeatActivityInput.checked = this.repeatActivity;
    }
  }

  public configuration: Configuration = {};
  public configurationVisible = true;
  public override tooltipContent: () => string = this._tooltipContent;

  public get activity(): Activity {
    return this._activity;
  }

  public set activity(value: Activity) {
    this._activity = value;
    this.configuration = clone(value.configuration);
  }

  public get optionIndex() {
    return this.activity?.options?.findIndex((o) => o === this.option);
  }

  public get totalDuration() {
    const duration = this.activity.getOptionDuration(this.optionIndex);
    return this.repeat * duration;
  }

  public get totalRepeat() {
    const duration = this.activity.getOptionDuration(this.optionIndex);
    return this.repeat * Math.max(1, duration);
  }

  // #endregion Properties (14)

  // #region Constructors (1)

  constructor() {
    super();
    setTimeout(() => {
      this.classList.add(this.activity.name);
    });
  }

  // #endregion Constructors (1)

  // #region Public Methods (2)

  public _tooltipContent() {
    if (this.activity.options.length > 0 && this.option) {
      return `${this.activity.optionsData[this.option].description}
      <br>${formatCostEffect(
        this.activity.optionsData[this.option],
        this.repeat
      )}`;
    }
    if (this.activity instanceof Batch) {
      return this.activity._batchTooltip;
    }
    return formatCostEffect(this.activity, this.repeat);
  }

  public override render() {
    return html`
      <div
        class="queue-item"
        ?data-active=${this.active}
        ?data-disabled=${!this.activity.canUseHere()}
      >
        <div class="drag-handle">⠿</div>
        <div class="queue-item-label">
          <div class="queue-item-label-header">
            <label
              class="fancy ${this.activity.name} ${[...this.activity.tags]
                .filter((tag) => tag.includes('Magic'))
                .join(' ')}"
              >${this.activity?.displayName}</label
            >
            ${this.activity?.options?.length > 0
              ? html`
                  <select
                    name="options"
                    class="fancy ${this.option}"
                    @change=${(event: Event) => {
                      this.option =
                        this.activity?.options?.[
                          Number((event.target as HTMLSelectElement).value)
                        ];
                    }}
                  >
                    ${this.activity?.options.map(
                      (option, index) => html`
                        <option
                          value=${index}
                          class=${this.activity?.options[index]}
                          ?selected=${index ==
                          this.activity.options.findIndex(
                            (o) => o === this.option
                          )}
                        >
                          ${option}
                        </option>
                      `
                    )}
                  </select>
                `
              : nothing}
          </div>
          <div class="nowrap">
            <span class="repeat ${this.repeat == 0 ? 'danger strong' : ''}"
              >${this.repeat}</span
            >
            <button
              @mousemove=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              @click=${this._onMore}
            >
              +
            </button>
            <button
              @mousemove=${(event: Event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              @click=${this._onLess}
            >
              -
            </button>
            <input
              id="repeat-activity-input"
              type="checkbox"
              class="filter"
              checked=${this.repeatActivity}
              @input=${(event: Event) => {
                if (!(event.target instanceof HTMLInputElement)) return;
                this.repeatActivity = Boolean(event.target.checked);
              }}
            />
            <label
              ?hidden=${!ri.settings.queue.showRepeatToggles}
              class="chip"
              for="repeat-activity-input"
              >${ri.settings.queue.smallRepeatToggles ? '↻' : 'Repeat'}</label
            >
          </div>
        </div>
        <smooth-progress
          ?hidden=${!ri.settings.queue.showProgressBars}
          class="${this.activity.name} ${[...this.activity.tags]
            .filter((tag) => tag.includes('Magic'))
            .join(' ')}"
          .value=${new Decimal(this.activeIndex).times(
            this.activity.stepLength
          )}
          .max=${new Decimal(this.totalDuration)}
          .text=${this.activity.duration > 0
            ? `${formatTime(this.activity.stepLength * this.activeIndex)} /
              ${formatTime(this.totalDuration)}`
            : `${this.activeIndex} / ${this.totalDuration}`}
        >
        </smooth-progress>
        <div
          class="queue-tools"
          ?hidden=${false && !ri.unlockedPanels.has('activities')}
        >
          <button
            @mousemove=${(event: Event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            @click=${this._onEarlier}
          >
            ▲
          </button>
          <button
            @mousemove=${(event: Event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            @click=${this._onLater}
          >
            ▼
          </button>
          <!-- <button
              @mousemove=${(event: Event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
              @click=${this._onSplit}
            >
              ⟷
            </button> -->
          <button
            @mousemove=${(event: Event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            @click=${this._onDelete}
          >
            X
          </button>
        </div>
      </div>
      <div class="configuration" ?hidden=${!this.configurationVisible}>
        ${(
          Object.entries(this.configuration ?? {}) as [
            string,
            boolean | BoundValue | Decimal | string | string[]
          ][]
        ).map(([pref, value]) => {
          switch (true) {
            case value == 'EDIT_LANDMARKS':
              return html`<select id="landmark-select">
                  ${landmarkNames
                    .filter(
                      (name) =>
                        ri.landmarks[name].navigatable &&
                        ri.landmarks[name].visible &&
                        !ri.landmarks[name].temporaryFinished$.value
                    )
                    .map(
                      (landmarkName) =>
                        html`<option value=${landmarkName}>
                          ${ri.landmarks[landmarkName].displayName}
                        </option>`
                    )}</select
                ><button
                  @click=${() => {
                    this.configuration.landmark = (
                      this.shadowRoot!.getElementById(
                        'landmark-select'
                      )! as HTMLInputElement
                    ).value;
                    this.requestUpdate();
                  }}
                >
                  Set
                </button>`;
            case typeof value === 'boolean':
              return html`<button
                @click=${() => {
                  this.configuration[pref] = !this.configuration[pref];
                  this.requestUpdate();
                  ri.requestUpdate();
                }}
                class="${value ? 'toggled' : ''}"
              >
                ${pref}
              </button>`;
            case value instanceof Decimal:
              return html` <label for=${pref}>
                ${pref}
                <input
                  id=${pref}
                  value=${value}
                  @input=${(event: InputEvent) => {
                    try {
                      if (event.target?.value == 'NaN') {
                        this.configuration[pref] = Decimal.NaN;
                        return;
                      }
                      const newValue = new Decimal(
                        event.target?.value ?? value
                      );
                      if (
                        isNaN(newValue.exponent) ||
                        isNaN(newValue.mantissa)
                      ) {
                        throw new Error(
                          'New Decimal config value cannot be NaN'
                        );
                      }
                      this.configuration[pref] = newValue;
                    } catch (e) {
                      // console.error(e);
                    }
                  }}
              /></label>`;
            case typeof value === 'string':
              return html` <label for=${pref}>
                ${pref}
                <input
                  id=${pref}
                  value=${value}
                  @input=${(event: InputEvent) => {
                    this.configuration[pref] = event.target?.value ?? value;
                  }}
              /></label>`;
            case 'value' in value:
              return html` <label for=${pref}>
                ${pref}
                <input
                  id=${pref}
                  type="range"
                  value=${value.value}
                  min=${value.min}
                  max=${value.max}
                  step="0.1"
                  @input=${(event: InputEvent) => {
                    this.configuration[pref].value =
                      event.target?.value ?? value.value;
                  }}
              /></label>`;
          }
        })}
      </div>
    `;
  }

  // #endregion Public Methods (2)

  // #region Private Methods (9)

  private _onDelete() {
    this.tooltip.destroy();
    const event = new Event('queue-item-deleted', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onEarlier() {
    const event = new Event('queue-item-earlier', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onLater() {
    const event = new Event('queue-item-later', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private _onLess(e: MouseEvent) {
    let numClicks = 1;
    if (e.ctrlKey) numClicks *= 5;
    if (e.shiftKey) numClicks *= 10;
    this.repeat -= numClicks;
    if (this.repeat < 0) this.repeat = 0;
  }

  private _onMore(e: MouseEvent) {
    let numClicks = 1;
    if (e.ctrlKey) numClicks *= 5;
    if (e.shiftKey) numClicks *= 10;
    this.repeat += numClicks;
    if (ri.lastGameSpeed) ri.gameSpeed = ri.lastGameSpeed;
  }

  private _onReorderDrop(e: CustomEvent) {
    setTimeout(() => {
      const queueItem =
        this.shadowRoot?.querySelector<HTMLElement>('.queue-item');
      if (queueItem) queueItem.style.visibility = 'visible';
      const dragHelper =
        this.shadowRoot?.querySelector<HTMLElement>('.drag-helper');
      if (dragHelper) dragHelper.style.visibility = 'hidden';
      const drop: Element = e.detail.dropElement;
      if (drop) {
        const event = new CustomEvent('queue-item-reorder', {
          detail: {
            from: Object.values(this.parentNode?.children ?? {}).indexOf(this),
            to: Object.values(this.parentNode?.children ?? {}).indexOf(drop),
          },
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(event);
      }
    });
  }

  private _onReorderMove(_: Event) {}

  private _onReorderStart(_: Event) {
    setTimeout(() => {
      const queueItem =
        this.shadowRoot?.querySelector<HTMLElement>('.queue-item');
      if (queueItem) queueItem.style.visibility = 'hidden';
      const dragHelper =
        this.shadowRoot?.querySelector<HTMLElement>('.drag-helper');
      if (dragHelper) dragHelper.style.visibility = 'visible';
    });
  }

  private _onSplit() {
    const event = new Event('queue-item-split', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  // #endregion Private Methods (9)
}
